import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    IconButton,
    Chip,
    Stack,
    Tooltip,
    alpha
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import CommonTable from '../UI/Table/CommonTable';
import FeedbackDialog from './FeedbackDialog';
import { getAssignments } from '../Queries';

const FeedbackTable = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [availableAssessments, setAvailableAssessments] = useState([]);

    let [userData] = useState(localStorage.getItem("userData"));
    userData = userData ? JSON.parse(userData) : userData;

    // Load initial data
    useEffect(() => {
        loadFeedbacks();
        loadActiveAssessments();
    }, []);

    const loadFeedbacks = async () => {
        setIsLoading(true);
        // Simulate API call - Replace with actual API
        setTimeout(() => {
            setFeedbacks([
                {
                    id: '1',
                    feedback_name: 'Q1 Performance Feedback',
                    assessment_id: '123',
                    assessment_name: 'Performance Assessment Q1',
                    program_code: 'TECH-2024',
                    template_name: 'Performance Review',
                    status: 'Active',
                    created_date: '2024-01-15',
                    responses: 12
                },
                {
                    id: '2',
                    feedback_name: 'Training Effectiveness Survey',
                    assessment_id: '124',
                    assessment_name: 'Training Assessment',
                    program_code: 'HR-2024',
                    template_name: 'Training Survey',
                    status: 'Draft',
                    created_date: '2024-01-20',
                    responses: 0
                }
            ]);
            setIsLoading(false);
        }, 1000);
    };

    const loadActiveAssessments = async () => {
        try {
            const assignments = await getAssignments();

            // Filter only assessments (not other assignment types)
            const assessmentsList = assignments.filter(
                assignment => assignment.assignment_topic === 'assessment'
            );

            // Filter to show only available assessments (based on dates)
            const activeAssessments = assessmentsList.filter(assignment => {
                const status = getAssessmentStatus(assignment);
                return status === 'available';
            });

            setAvailableAssessments(activeAssessments);
        } catch (error) {
            console.error('Error loading assessments:', error);
            setAvailableAssessments([]);
        }
    };

    // Helper function to determine assessment status based on dates
    const getAssessmentStatus = (assessment) => {
        const { startDate, endDate } = getAssessmentDates(assessment);

        if (!startDate || !endDate) {
            return 'available';
        }

        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (now < start) {
            return 'upcoming';
        } else if (now > end) {
            return 'closed';
        } else {
            return 'available';
        }
    };

    // Helper function to extract dates from assessment
    const getAssessmentDates = (assessment) => {
        let startDate = null;
        let endDate = null;

        try {
            if (assessment.json) {
                const jsonData = JSON.parse(assessment.json);
                if (jsonData.startDate) startDate = jsonData.startDate;
                if (jsonData.endDate) endDate = jsonData.endDate;
            }
        } catch (error) {
            console.error('Error parsing assessment JSON:', error);
        }

        return { startDate, endDate };
    };

    const handleCreateFeedback = () => {
        setSelectedFeedback(null);
        setOpenDialog(true);
    };

    const handleEditFeedback = (feedback) => {
        setSelectedFeedback(feedback);
        setOpenDialog(true);
    };

    const handleDeleteFeedback = (feedbackId) => {
        if (window.confirm('Are you sure you want to delete this feedback form?')) {
            setFeedbacks(prev => prev.filter(f => f.id !== feedbackId));
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedFeedback(null);
    };

    const handleSaveFeedback = (feedbackData) => {
        if (selectedFeedback) {
            // Update existing feedback
            setFeedbacks(prev => prev.map(f =>
                f.id === selectedFeedback.id ? { ...f, ...feedbackData } : f
            ));
        } else {
            // Create new feedback
            const newFeedback = {
                id: Date.now().toString(),
                ...feedbackData,
                created_date: new Date().toISOString().split('T')[0],
                responses: 0
            };
            setFeedbacks(prev => [...prev, newFeedback]);
        }
        handleCloseDialog();
    };

    // Define columns
    const columns = useMemo(() => {
        const baseColumns = [
            { key: 'serialNo', label: 'S.No', minWidth: 70, align: 'center' },
            { key: 'feedback_name', label: 'Feedback Name', minWidth: 250 }
        ];

        if (userData.user_role !== "User") {
            baseColumns.push(
                { key: 'program_code', label: 'Program Code', minWidth: 150 },
                { key: 'template_name', label: 'Template Name', minWidth: 150 }
            );
        }

        baseColumns.push(
            { key: 'created_date', label: 'Created Date', minWidth: 120 },
            { key: 'responses', label: 'Responses', minWidth: 100 },
            { key: 'status', label: 'Status', minWidth: 100 },
            { key: 'actions', label: 'Actions', minWidth: 150 }
        );

        return baseColumns;
    }, [userData.user_role]);

    // Cell rendering function
    const renderCell = (column, row, index) => {
        switch (column.key) {
            case 'serialNo':
                return (
                    <Box sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        fontSize: '0.85rem'
                    }}>
                        {index + 1}
                    </Box>
                );

            case 'feedback_name':
                return (
                    <Box>
                        <Box sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: '#2c3e50'
                        }}>
                            {row.feedback_name}
                        </Box>
                    </Box>
                );

            case 'status':
                const statusColors = {
                    'Active': { bg: alpha('#4caf50', 0.1), text: '#2e7d32', border: alpha('#4caf50', 0.3) },
                    'Draft': { bg: alpha('#ff9800', 0.1), text: '#e65100', border: alpha('#ff9800', 0.3) },
                    'Closed': { bg: alpha('#9e9e9e', 0.1), text: '#424242', border: alpha('#9e9e9e', 0.3) }
                };
                const colorConfig = statusColors[row.status] || statusColors['Draft'];

                return (
                    <Chip
                        label={row.status}
                        size="small"
                        sx={{
                            bgcolor: colorConfig.bg,
                            color: colorConfig.text,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            border: `1px solid ${colorConfig.border}`,
                            height: 24,
                            '& .MuiChip-label': {
                                px: 1.5,
                                fontSize: '0.7rem',
                                fontWeight: 700
                            }
                        }}
                    />
                );

            case 'responses':
                return (
                    <Box sx={{
                        fontWeight: 600,
                        color: row.responses > 0 ? 'primary.main' : 'text.secondary'
                    }}>
                        {row.responses}
                    </Box>
                );

            case 'actions':
                return (
                    <Stack direction="row" spacing={1}>
                        {userData.user_role !== "User" && (
                            <>
                                <Tooltip title="Edit Feedback" arrow placement="top">
                                    <IconButton
                                        onClick={() => handleEditFeedback(row)}
                                        size="small"
                                        sx={{
                                            padding: '6px',
                                            backgroundColor: alpha('#cc4049', 0.1),
                                            color: '#cc4049',
                                            border: `1px solid ${alpha('#cc4049', 0.3)}`,
                                            borderRadius: '8px',
                                            '&:hover': {
                                                backgroundColor: alpha('#cc4049', 0.2),
                                                transform: 'scale(1.1)',
                                                boxShadow: '0 2px 8px rgba(204, 64, 73, 0.3)',
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Feedback" arrow placement="top">
                                    <IconButton
                                        onClick={() => handleDeleteFeedback(row.id)}
                                        size="small"
                                        sx={{
                                            padding: '6px',
                                            backgroundColor: alpha('#f44336', 0.1),
                                            color: '#f44336',
                                            border: `1px solid ${alpha('#f44336', 0.3)}`,
                                            borderRadius: '8px',
                                            '&:hover': {
                                                backgroundColor: alpha('#f44336', 0.2),
                                                transform: 'scale(1.1)',
                                                boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        <Tooltip title="View Responses" arrow placement="top">
                            <IconButton
                                size="small"
                                disabled={row.responses === 0}
                                sx={{
                                    padding: '6px',
                                    backgroundColor: alpha('#17a2b8', 0.1),
                                    color: '#17a2b8',
                                    border: `1px solid ${alpha('#17a2b8', 0.3)}`,
                                    borderRadius: '8px',
                                    '&:hover': {
                                        backgroundColor: alpha('#17a2b8', 0.2),
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 2px 8px rgba(23, 162, 184, 0.3)',
                                    },
                                    '&:disabled': {
                                        opacity: 0.5,
                                        backgroundColor: alpha('#17a2b8', 0.05)
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );

            default:
                return row[column.key];
        }
    };

    return (
        <>
            <CommonTable
                columns={columns}
                data={feedbacks}
                renderCell={renderCell}
                isLoading={isLoading}
                emptyMessage="No feedback forms created yet"
                actionButtons={
                    userData.user_role !== "User" && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                            onClick={handleCreateFeedback}
                            size="small"
                            sx={{
                                textTransform: "none",
                                fontWeight: 500,
                                borderRadius: 1,
                                px: 2,
                                fontSize: '13px',
                                py: 0.75,
                                background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                                "&:hover": {
                                    background: "linear-gradient(45deg, #1565c0 30%, #0a0068 90%)",
                                },
                            }}
                        >
                            Create Feedback
                        </Button>
                    )
                }
            />

            <FeedbackDialog
                open={openDialog}
                onClose={handleCloseDialog}
                onSave={handleSaveFeedback}
                feedback={selectedFeedback}
                availableAssessments={availableAssessments}
            />
        </>
    );
};

export default FeedbackTable;
